import { Geom, Scene } from 'phaser';
import { AreaRolavel } from '../components/AreaRolavel';
import { BotaoArcade } from '../components/BotaoArcade';
import { CartaoOpcao } from '../components/CartaoOpcao';
import { BORDA_DIR, BORDA_ESQ, desenharImagemDeFundo, desenharPaginaLima } from '../components/cenario';
import { ModalArcade } from '../components/ModalArcade';
import { MolduraPixel } from '../components/MolduraPixel';
import { criarTextoRico } from '../components/TextoRico';
import { GAME, SCENES, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { abrirCamera, fecharCamera, recorteCover } from '../services/cameraEspelho';
import { enviarHabilitado, paraAfirmacoesDoPixel3 } from '../services/menuResolucao';
import { formatarTempo } from '../services/labPixels';
import { Sessao } from '../services/Sessao';

/**
 * Menu Resolução do participante (Figma do jogador, arquivo XoDUKf460ZfTreQ7XNn4wV).
 * Segue o que o facilitador libera pela fonte do modo `dev` (ponte): janela do Pixel,
 * surpresa por grupo, resultado e fim do módulo. Respostas ficam só no tablet até a fonte
 * de produção.
 *
 * Cabeçalho, cartões, modal e botões vêm dos componentes do Lab Pixels; as posições são as
 * dos quadros do Figma (get_metadata), em px de um quadro 1280×800.
 *
 * O telão ("VER QUEM FAZ A DIFERENÇA") é um espelho da câmera frontal do tablet; a imagem não sai
 * do aparelho. Sem câmera ou sem permissão, a área fica escura.
 *
 * Assets ainda não exportados (a tela funciona sem eles, com um espaço reservado):
 * `menu-resolucao-console` (console arcade do Pixel 3) e `menu-resolucao-presente` (ícone do modal
 * de surpresa). Ver TEX.MR_* e o manifesto de assets.
 */

/** Posições por Pixel: y da pergunta, y do 1º cartão e y do botão Enviar (Figma, quadros 2, 5, 20 e 24). */
const LAYOUT = {
    1: { pergunta: 244, cartoes: 319, botao: 689 },
    2: { pergunta: 244, cartoes: 298, botao: 714 },
    4: { pergunta: 244, cartoes: 344, botao: 698 },
    5: { pergunta: 251, cartoes: 327, botao: 697 }
};
const COLUNAS_X = [62, 648];
const PASSO_Y = 82;
const CARTAO = { largura: 570, altura: 72 };
/** Resultado do Pixel 3: a lista rola abaixo das abas; `cartao` é a altura dos dois cartões de cada afirmação. */
const AFIRMACOES = { abas: 200, areaX: 40, areaY: 288, areaLargura: 1200, cartao: 66, folga: 10, entre: 26 };
const ROTULO_CONTROLO = 'Controlo';
const ROTULO_NAO_CONTROLO = 'Não controlo';

const MENSAGEM_ESPERA = 'Aguarde. A próxima atividade do Menu Resolução aparece aqui.';
const MENSAGEM_PAUSA = 'Atividade pausada pelo facilitador. Aguarde a retomada.';
const PRESENTE_ALTURA = 120;
const AFIRMACAO = { x: 221, y: 290, largura: 836, altura: 159 };
const TELAO_LINHAS = 'VOCÊ É FUNDAMENTAL PARA\nO SUCESSO DO NOVO FIAT ARGO X!';

/**
 * Moldura do telão (Figma, quadros 18 e 19): a foto fica numa área de 1206×731 com borda branca
 * e recortes em degrau no meio de cada lado; fora dela, verde-lima chapado. Contorno medido
 * sobre o screenshot do quadro, em px de 1280×800.
 */
const TELAO = {
    area: { x: 38, y: 31, largura: 1206, altura: 729 },
    contorno: [
        [38, 31], [388, 31], [460, 53], [820, 53], [892, 31], [1244, 31],
        [1244, 140], [1221, 220], [1221, 580], [1244, 652],
        [1244, 760], [890, 760], [812, 739], [470, 739], [390, 760], [38, 760],
        [38, 652], [60, 580], [60, 220], [38, 140]
    ],
    faixas: [
        [[0, 0], [1280, 0], [1280, 31], [892, 31], [820, 53], [460, 53], [388, 31], [0, 31]],
        [[0, 760], [390, 760], [470, 739], [812, 739], [890, 760], [1280, 760], [1280, 800], [0, 800]],
        [[0, 0], [38, 0], [38, 140], [60, 220], [60, 580], [38, 652], [38, 800], [0, 800]],
        [[1280, 0], [1244, 0], [1244, 140], [1221, 220], [1221, 580], [1244, 652], [1244, 800], [1280, 800]]
    ],
    lima: 0xd2ff75
};

const INSTRUCAO = [
    { texto: 'Ao longo deste módulo você vai conectar quatro Pixels.', peso: '600', maiusculas: true },
    { texto: 'Cada atividade ajuda a aumentar a resolução:\nmercado, cliente, controle profissional e Fiat.' },
    { texto: 'Responda pelo seu tablet quando a atividade for liberada.' },
    // O Figma traz uma aspa de fechamento solta ao fim deste trecho; omitida (a confirmar com o design).
    { texto: 'Algumas respostas revelam a leitura coletiva da turma;\noutras testam o que realmente está nas suas mãos.' }
];
const RESOLUCAO = [
    ['Vocês conectaram os 4 Pixels e **ampliaram a resolução da jornada.**'],
    ['Ao longo das atividades, a turma construiu uma **leitura sobre o mercado, os diferentes critérios de decisão do cliente, o que está sob controle do consultor e as provas concretas de valor da Fiat.**'],
    ['Agora, a imagem está completa:', '**mercado, cliente, profissional e marca conectados em uma visão única.**']
];

/* Memória que sobrevive a sair e voltar para a cena. */
const instrucaoVista = new Set();
const enviadas = new Set();        // `${participante}:${pixel}:${rodada}`
const respondidas = new Set();     // `${participante}:${pixel}`
const afirmacaoAtual = new Map();  // rodadaId -> próxima afirmação (Pixel 3)
const respostasAfirmacoes = new Map(); // rodadaId -> 'controlo' | 'nao_controlo' por afirmação
const telaoVisto = new Set();      // rodadaId

const participanteAtual = () => Sessao.atual?.participanteId ?? 'anonimo';

/** Grupo do jogador; a sessão de teste (grupo G00) acompanha o Grupo 1 do facilitador mockado. */
const grupoAtual = () => (Sessao.ehTeste ? 'G01' : Sessao.atual?.grupoId);

export class MenuResolucaoScene extends Scene {
    constructor () {
        super(SCENES.MENU_RESOLUCAO);
    }

    create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.painel = null;
        this.assinatura = null;
        this.visao = 'turma';
        this.objetos = [];
        this.cartoes = [];
        this.selecao = new Set();
        this.botaoEnviar = null;
        this.cronometro = null;
        this.modal = null;
        this.espelho = null;
        this.telao = null;
        this.ativo = true;

        desenharPaginaLima(this, {
            titulo: 'Menu Resolução', rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0
        });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.modal && this.scene.start(SCENES.MURAL, { voltarPara: SCENES.MENU_RESOLUCAO }));

        this.relogio = this.time.addEvent({ delay: 1000, loop: true, callback: () => this.sincronizar() });
        this.events.once('shutdown', () => {
            this.ativo = false;
            this.relogio?.remove(false);
            this.fecharEspelho();
        });
        this.sincronizar();
    }

    async sincronizar () {
        const painel = (await this.fonte.obterMenuResolucao?.()) ?? null;
        if (!this.ativo) return;
        this.receber(painel);
    }

    receber (painel) {
        this.painel = painel;
        // No telão a tela não troca sozinha: o jogador segue quando toca em Próximo pixel.
        if (this.telao) return this.atualizarTelao();
        const assinatura = this.assinaturaDe(painel);
        if (assinatura !== this.assinatura) {
            this.assinatura = assinatura;
            this.renderizar();
        } else if (this.cronometro && painel?.janela === 'aberta') {
            this.atualizarCronometro(painel.tempoRestante);
        }
    }

    /** Muda quando o que aparece muda (janela, rodada, surpresa, resultado, fim do módulo). */
    assinaturaDe (painel) {
        if (!painel) return 'vazio';
        const grupo = grupoAtual();
        return JSON.stringify([
            painel.pixel, painel.rodada, painel.janela, painel.moduloEncerrado, painel.moduloFinalizado, painel.tempoRestante === 0, Boolean(painel.pausada),
            Boolean(painel.catalogo),
            Boolean(painel.resultados?.[painel.pixel]), Boolean(painel.surpresaLiberada?.[grupo])
        ]);
    }

    renderizar () {
        this.limpar();
        const painel = this.painel;
        const participante = participanteAtual();
        if (!painel) return this.mostrarEspera();
        // Só o facilitador devolve o participante ao Aguarde (FINALIZAR): a tela de pontuação não tem saída própria.
        if (painel.moduloFinalizado) return this.scene.start(SCENES.AGUARDE, { concluida: 'menuresolucao' });
        if (painel.moduloEncerrado) return this.mostrarResolucaoCompleta(participante);
        if (painel.pixel == null || painel.janela === 'nao_aberta') return this.mostrarEspera();
        // Janela pausada pelo facilitador: o tempo parou e a tela não oferece a pergunta até retomar.
        if (painel.janela === 'aberta' && painel.pausada) return this.mostrarPausa();
        return painel.janela === 'aberta' ? this.mostrarAtividade(participante) : this.mostrarEncerrado(participante);
    }

    limpar () {
        this.telao = null;
        this.senhaTela = null;
        this.fecharEspelho();
        this.modal?.camada.destroy();
        this.modal = null;
        // A lista rolável dos resultados do Pixel 3 é refeita a cada render: tira os ouvintes dela.
        this.rolagem?.destruir();
        this.rolagem = null;
        this.objetos.forEach((objeto) => objeto.destroy());
        this.objetos = [];
        this.cartoes = [];
        this.botaoEnviar?.destroy();
        this.botaoEnviar = null;
        this.cronometro?.texto.destroy();
        this.cronometro?.relogio.destroy();
        this.cronometro = null;
        this.selecao = new Set();
    }

    /* ---------- peças comuns ---------- */

    guardar (...objetos) {
        this.objetos.push(...objetos);
        return objetos[0];
    }

    linha (largura = 1156, y = 221) {
        return this.guardar(this.add.rectangle(62, y, largura, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5));
    }

    /** Título de seção em Barlow ("RESULTADOS", "RESOLUÇÃO COMPLETA"), com a linha divisória. */
    tituloDeSecao (texto, larguraLinha = 1156) {
        this.guardar(this.add.text(62, 185, texto, ARCADE_TEXT.rotuloPergunta).setOrigin(0, 0.5));
        this.linha(larguraLinha);
    }

    /** "PIXEL 1/4 - MERCADO" + "i" + cronômetro + linha (no Pixel 3 o bloco desce 20 px). */
    cabecalhoDoPixel () {
        const { pixel, catalogo, tempoRestante } = this.painel;
        const dy = pixel === 3 ? 20 : 0;
        const rotulo = pixel === 5 ? 'PIXEL EXTRA' : `PIXEL ${pixel}/4 - ${catalogo.titulo.toUpperCase()}`;
        const texto = this.add.text(63, 185 + dy, rotulo, ARCADE_TEXT.tituloFase).setOrigin(0, 0.5);
        const icone = this.add.image(texto.x + texto.width + 8, 185 + dy, TEX.ICON_INFO_PIXEL).setOrigin(0, 0.5);
        // O "i" reabre a instrução do módulo.
        const zona = this.add.zone(63, 185 + dy, texto.width + 48, 48).setOrigin(0, 0.5)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.abrirInstrucao());
        this.guardar(texto, icone, zona);
        this.linha(1156, 221 + dy);

        const numeros = this.add.text(1216, 185 + dy, '', ARCADE_TEXT.cronometro).setOrigin(1, 0.5);
        this.cronometro = { texto: numeros, relogio: this.add.graphics(), dy };
        this.atualizarCronometro(tempoRestante);
    }

    atualizarCronometro (segundos) {
        const { texto, relogio, dy } = this.cronometro;
        texto.setText(formatarTempo(segundos ?? 0));
        const cx = texto.x - texto.width - 22;
        relogio.clear().lineStyle(2.6, ARCADE_HEX.TINTA, 1).strokeCircle(cx, 188 + dy, 11)
            .lineBetween(cx, 181.5 + dy, cx, 189 + dy).lineBetween(cx, 189 + dy, cx + 6, 189 + dy);
    }

    botaoPadrao (y, rotulo, config) {
        const botao = new BotaoArcade(this, 640, y + 35, { rotulo, largura: 318, ...config });
        this.guardar(botao);
        return botao;
    }

    mostrarEspera () {
        this.guardar(this.add.text(640, 400, MENSAGEM_ESPERA, { ...ARCADE_TEXT.aviso, wordWrap: { width: 900 } }).setOrigin(0.5));
    }

    mostrarPausa () {
        this.guardar(this.add.text(640, 400, MENSAGEM_PAUSA, { ...ARCADE_TEXT.aviso, wordWrap: { width: 900 } }).setOrigin(0.5));
    }

    /* ---------- instrução do módulo ---------- */

    abrirInstrucao (aoIniciar = null) {
        if (this.modal) return;
        this.modal = new ModalArcade(this, {
            titulo: 'Menu Resolução',
            largura: 826,
            centroY: 373,
            botaoY: 601,
            blocos: INSTRUCAO,
            botao: { rotulo: 'INICIAR', aoClicar: aoIniciar },
            aoFechar: () => { this.modal = null; }
        });
    }

    /* ---------- janela aberta ---------- */

    mostrarAtividade (participante) {
        const { pixel, rodada, catalogo } = this.painel;
        if (!catalogo) return this.mostrarEspera();
        const rodadaId = `${participante}:${pixel}:${rodada}`;
        // O tablet pode ter sido reaberto depois de enviar: a fila de envio sabe, a memória da cena não.
        if (this.fonte.respostaMenuResolucaoEnviada?.({ pixel, rodada })) enviadas.add(rodadaId);
        // O limite da resposta passou (a janela ainda fica aberta uns segundos para os envios): o que vier
        // depois não vale, então a tela não oferece mais a pergunta.
        if (this.painel.tempoRestante === 0 && !catalogo.surpresa && !enviadas.has(rodadaId)) return this.mostrarResultadoBloqueado();

        if (catalogo.surpresa) {
            if (telaoVisto.has(rodadaId)) return this.mostrarEspera();
            const indice = afirmacaoAtual.get(rodadaId) ?? 0;
            // Respondeu todas: a tela de Resultados (bloqueada até o facilitador liberar), como nos demais Pixels.
            if (indice >= catalogo.afirmacoes.length) return this.mostrarResultadoBloqueado();
            this.desenharAfirmacao(catalogo.afirmacoes[indice], (indiceAtual, valor) => this.responderAfirmacao(rodadaId, indiceAtual, valor), indice);
        } else if (enviadas.has(rodadaId)) {
            // Respondeu e o resultado ainda não saiu: a própria tela de Resultados, com as opções
            // bloqueadas (Figma), em vez de um aviso de espera à parte.
            return this.mostrarResultadoBloqueado();
        } else {
            this.mostrarPergunta(rodadaId);
        }
        // A atividade abre pela instrução; ela só aparece na primeira vez de cada participante.
        if (!instrucaoVista.has(participante)) {
            this.abrirInstrucao(() => instrucaoVista.add(participante));
        }
    }

    /* ---------- Pixels 1, 2, 4 e Extra: pergunta e alternativas ---------- */

    mostrarPergunta (rodadaId) {
        const { pixel, catalogo } = this.painel;
        const medidas = LAYOUT[pixel] ?? LAYOUT[1];
        this.cabecalhoDoPixel();

        const enunciado = criarTextoRico(this, 62, medidas.pergunta - 2, 1180, catalogo.enunciado ?? catalogo.pergunta, ARCADE_TEXT.introducao);
        this.guardar(...enunciado.objetos);

        const caixa = catalogo.quantidade_selecao > 1;
        catalogo.opcoes.forEach((opcao, i) => {
            const cartao = new CartaoOpcao(this, COLUNAS_X[i % 2], medidas.cartoes + Math.floor(i / 2) * PASSO_Y, {
                letra: opcao.letra ?? opcao.id, texto: opcao.texto, ...CARTAO, centrado: true, caixa,
                aoTocar: () => this.alternar(opcao.id, rodadaId)
            });
            cartao.opcaoId = opcao.id;
            this.cartoes.push(cartao);
        });
        this.guardar(...this.cartoes);
        this.atualizarEnviar(rodadaId);
    }

    alternar (id, rodadaId) {
        const limite = this.painel.catalogo.quantidade_selecao;
        if (this.selecao.has(id)) {
            this.selecao.delete(id);
        } else if (limite === 1) {
            this.selecao = new Set([id]);
        } else if (this.selecao.size < limite) {
            this.selecao.add(id);
        }
        // Com várias escolhas, ao completar a cota as demais ficam esmaecidas.
        // No Figma (quadros 5 e 6) a escolha múltipla não pinta o cartão: ele segue branco e só a
        // caixinha vira azul. Na escolha única (sem caixinha) o cartão inteiro fica azul.
        const cheio = limite > 1 && this.selecao.size >= limite;
        this.cartoes.forEach((cartao) => {
            const marcada = this.selecao.has(cartao.opcaoId);
            cartao.setEstado(marcada && limite === 1 ? 'selecionada' : 'normal').setMarcada(marcada);
            cartao.setAlpha(cheio && !marcada ? 0.55 : 1);
        });
        this.atualizarEnviar(rodadaId);
    }

    atualizarEnviar (rodadaId) {
        this.botaoEnviar?.destroy();
        const { pixel, catalogo } = this.painel;
        const perguntas = [{
            id: 'p', quantidade_selecao: catalogo.quantidade_selecao, opcoes: catalogo.opcoes.map(({ id }) => ({ id }))
        }];
        const respostas = [...this.selecao].map((opcao_id) => ({ pergunta_id: 'p', opcao_id }));
        const habilitado = enviarHabilitado({ pixel, perguntas, respostas });
        const medidas = LAYOUT[pixel] ?? LAYOUT[1];
        this.botaoEnviar = new BotaoArcade(this, 640, medidas.botao + 35, {
            rotulo: 'ENVIAR', largura: 318, habilitado, variante: 'cinzaApagado',
            aoClicar: () => this.enviar(rodadaId)
        });
    }

    enviar (rodadaId) {
        const { pixel, rodada } = this.painel;
        enviadas.add(rodadaId);
        respondidas.add(`${participanteAtual()}:${pixel}`);
        // Em `prd` o envio vai para a fila do tablet (chave fixa por Pixel e rodada); em `dev` não faz nada.
        this.fonte.registrarRespostaPixel?.({ pixel, rodada, opcoes: [...this.selecao] });
        this.renderizar();
    }

    /* ---------- Pixel 3: afirmações e surpresa ---------- */

    /** Botão achatado do console arcade (Controlo / Não controlo). */
    botaoConsole (x, y, rotulo, { topo, base }, aoClicar) {
        const largura = 312, altura = 52, ALTURA_FACE = altura - 9, AFUNDA = 6;
        // Base (a "lateral" do botão) fica parada; a face desce ao apertar, como num botão físico.
        const fundo = this.add.graphics();
        fundo.fillStyle(base, 1).fillRoundedRect(0, 0, largura, altura, 5);
        const desenharFace = (cor, brilho) => {
            const face = this.add.graphics();
            face.fillStyle(cor, 1).fillRoundedRect(0, 0, largura, ALTURA_FACE, 5);
            face.lineStyle(1.5, ARCADE_HEX.BRANCO, brilho).strokeRoundedRect(0.75, 0.75, largura - 1.5, ALTURA_FACE - 1.5, 5);
            return face;
        };
        const face = desenharFace(topo, 0.45);
        const texto = this.add.text(largura / 2, ALTURA_FACE / 2, rotulo, { ...ARCADE_TEXT.botao, fontSize: '22px' }).setOrigin(0.5);
        const tampa = this.add.container(0, 0, [face, texto]);
        const caixaBotao = this.add.container(x, y, [fundo, tampa]).setSize(largura, altura);
        if (!aoClicar) return caixaBotao;

        let apertado = false;
        let enviado = false;
        const mover = (destino, duracao, ease) => {
            this.tweens.killTweensOf(tampa);
            this.tweens.add({ targets: tampa, y: destino, duration: duracao, ease });
        };
        const apertar = () => {
            if (enviado) return;
            apertado = true;
            mover(AFUNDA, 55, 'Quad.easeOut');
            tampa.setAlpha(0.92);
        };
        const soltar = () => {
            if (!apertado) return;
            apertado = false;
            tampa.setAlpha(1);
            mover(0, 110, 'Back.easeOut');
        };
        // O Container testa o clique com a origem no centro (soma metade do tamanho ao ponto), mas o botão é
        // desenhado a partir do canto: sem este deslocamento a área clicável fica meio botão para a esquerda/cima,
        // e o toque na metade direita de um botão cai no vizinho.
        caixaBotao.setInteractive({
            useHandCursor: true,
            hitArea: new Geom.Rectangle(largura / 2, altura / 2, largura, altura),
            hitAreaCallback: Geom.Rectangle.Contains
        });
        caixaBotao.on('pointerdown', apertar);
        caixaBotao.on('pointerout', soltar);
        caixaBotao.on('pointerup', () => {
            if (!apertado || enviado) return;
            enviado = true;
            soltar();
            // Deixa o botão subir antes de trocar a afirmação.
            this.time.delayedCall(150, aoClicar);
        });
        return caixaBotao;
    }

    /** Quadro do Pixel 3: painel escuro com a afirmação, console e os dois botões. */
    desenharAfirmacao (texto, aoResponder, indice) {
        this.cabecalhoDoPixel();
        const g = this.add.graphics();
        MolduraPixel.desenhar(g, AFIRMACAO.x, AFIRMACAO.y, AFIRMACAO.largura, AFIRMACAO.altura,
            { fundo: 0x25271c, fio: ARCADE_HEX.BRANCO, fioMeio: 0x6f705f }, { esq: true, dir: true, tamanho: 11 });
        const afirmacao = this.add.text(259, AFIRMACAO.y + AFIRMACAO.altura / 2, `${indice + 1}. ${texto}`, {
            ...ARCADE_TEXT.introducao, color: ARCADE.BRANCO, fontStyle: '600', wordWrap: { width: 757 }, lineSpacing: 26
        }).setOrigin(0, 0.5);
        this.guardar(g, afirmacao);

        // Progresso "N de M": o participante vê quantas afirmações faltam.
        const total = this.painel.catalogo.afirmacoes.length;
        if (total > 0) {
            this.guardar(this.add.text(AFIRMACAO.x + AFIRMACAO.largura / 2, AFIRMACAO.y - 26,
                `${Math.min(indice, total - 1) + 1} de ${total}`,
                { ...ARCADE_TEXT.introducao, fontStyle: '700' }).setOrigin(0.5));
        }

        // O console arcade (image 1501) entra por cima do painel; sem o arquivo, só os botões.
        if (this.textures.exists(TEX.MR_CONSOLE)) this.guardar(this.add.image(129, 429, TEX.MR_CONSOLE).setOrigin(0));
        this.guardar(
            this.botaoConsole(306.43, 559, 'CONTROLO', { topo: 0x5f9a54, base: 0x3f7a35 }, aoResponder && (() => aoResponder(indice, 'controlo'))),
            this.botaoConsole(646.43, 559, 'NÃO CONTROLO', { topo: 0xc9252b, base: 0x8d1b1f }, aoResponder && (() => aoResponder(indice, 'nao_controlo')))
        );
    }

    responderAfirmacao (rodadaId, indice, valor) {
        afirmacaoAtual.set(rodadaId, indice + 1);
        const respostas = respostasAfirmacoes.get(rodadaId) ?? [];
        respostas[indice] = valor;
        respostasAfirmacoes.set(rodadaId, respostas);
        if (indice + 1 >= this.painel.catalogo.afirmacoes.length) {
            enviadas.add(rodadaId);
            respondidas.add(`${participanteAtual()}:${this.painel.pixel}`);
            this.fonte.registrarRespostasPixel3?.({
                participanteId: participanteAtual(), pixel: this.painel.pixel, rodada: this.painel.rodada, respostas
            });
        }
        this.renderizar();
    }

    /** Modal da surpresa (Figma, quadro 17), sobre o ranking, só depois de o facilitador liberá-la. */
    mostrarModalSurpresa (rodadaId) {
        const presente = this.textures.exists(TEX.MR_PRESENTE) ? { imagem: TEX.MR_PRESENTE } : { vazio: PRESENTE_ALTURA };
        this.modal = new ModalArcade(this, {
            titulo: 'Menu Resolução',
            tituloY: 220,
            painel: { x: 130, y: 148, largura: 1020, altura: 505 },
            largura: 506,
            centroY: 384.5,
            alturaMaxima: 220,
            botaoY: 570,
            blocos: [presente, { texto: 'O facilitador liberou uma surpresa!', largura: 560 }],
            botao: {
                rotulo: 'VER QUEM FAZ A DIFERENÇA', largura: 466,
                aoClicar: () => this.mostrarTelao(rodadaId)
            },
            aoFechar: () => { this.modal = null; }
        });
    }

    /**
     * Telão (Figma, quadros 18 e 19): espelho da câmera, mensagem e, só depois que o facilitador
     * avança, o botão Próximo pixel (quadro 19). Antes disso a tela fica como no quadro 18.
     */
    mostrarTelao (rodadaId) {
        this.limpar();
        const { area } = TELAO;
        desenharImagemDeFundo(this, TEX.BG_LIMA).forEach((imagem) => this.guardar(imagem));
        const reserva = this.add.rectangle(area.x, area.y, area.largura, area.altura, 0x34352c).setOrigin(0);
        // Escurece a parte de baixo, onde fica a mensagem, como no Figma.
        const sombra = this.add.graphics();
        sombra.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.75, 0.75)
            .fillRect(area.x, area.y + area.altura - 325, area.largura, 325);
        const moldura = this.add.graphics();
        moldura.fillStyle(TELAO.lima, 1);
        // As faixas das bordas se estendem pela margem da tela larga (celular).
        const naBorda = (x) => (x <= 0 ? BORDA_ESQ : x >= GAME.WIDTH ? BORDA_DIR : x);
        TELAO.faixas.forEach((pontos) => moldura.fillPoints(pontos.map(([x, y]) => new Geom.Point(naBorda(x), y)), true));
        moldura.lineStyle(3, ARCADE_HEX.BRANCO, 1)
            .strokePoints(TELAO.contorno.map(([x, y]) => new Geom.Point(x, y)), true);
        const mensagem = this.add.text(640, 624, TELAO_LINHAS, {
            ...ARCADE_TEXT.tituloPagina, fontSize: '44px', align: 'center', lineSpacing: 0,
            shadow: { offsetX: 3, offsetY: 3, color: ARCADE.VERDE, blur: 0, fill: true }
        }).setOrigin(0.5);
        this.guardar(reserva, sombra, moldura, mensagem);
        this.telao = { rodadaId, pixel: this.painel.pixel, botao: null };
        this.abrirEspelho(area, sombra);
        this.atualizarTelao();
    }

    /** O facilitador liberou quando o painel já passou do Pixel do telão (ou o módulo acabou). */
    atualizarTelao () {
        const telao = this.telao;
        if (!telao || telao.botao) return;
        const liberado = this.painel?.pixel !== telao.pixel || Boolean(this.painel?.moduloEncerrado);
        if (!liberado) return;
        telao.botao = this.botaoPadrao(699, 'PRÓXIMO PIXEL', {
            aoClicar: () => {
                telaoVisto.add(telao.rodadaId);
                this.assinatura = this.assinaturaDe(this.painel);
                this.renderizar();
            }
        });
        telao.botao.setAlpha(0);
        this.tweens.add({ targets: telao.botao, alpha: 1, duration: 250 });
    }

    /** Liga a câmera frontal na área do telão, espelhada; sem câmera, nada muda. */
    async abrirEspelho (area, acima) {
        const senha = Symbol('espelho');
        this.senhaEspelho = senha;
        const stream = await abrirCamera();
        if (!stream) return;
        // A tela pode ter mudado enquanto a permissão era pedida.
        if (!this.ativo || this.senhaEspelho !== senha) {
            fecharCamera(stream);
            return;
        }
        const video = this.add.video(area.x + area.largura / 2, area.y + area.altura / 2).setFlipX(true);
        video.on('created', (_, w, h) => {
            const r = recorteCover({ w, h, largura: area.largura, altura: area.altura });
            video.setCrop(r.x, r.y, r.largura, r.altura).setScale(r.escala);
        });
        video.loadMediaStream(stream, true);
        video.play(true);
        // O vídeo fica entre o fundo escuro e a sombra da mensagem.
        this.children.moveTo(video, this.children.getIndex(acima));
        this.espelho = { video, stream };
    }

    fecharEspelho () {
        this.senhaEspelho = null;
        if (!this.espelho) return;
        fecharCamera(this.espelho.stream);
        this.espelho.video.destroy();
        this.espelho = null;
    }

    /* ---------- janela encerrada: resultado ---------- */

    mostrarEncerrado (participante) {
        const { pixel, rodada, catalogo, resultados } = this.painel;
        const rodadaId = `${participante}:${pixel}:${rodada}`;
        if (catalogo?.surpresa) {
            if (telaoVisto.has(rodadaId)) return this.mostrarEspera();
            // Primeiro o ranking das afirmações (quando o facilitador libera); o modal da surpresa só
            // aparece por cima dele quando o facilitador libera a surpresa.
            const ranking = resultados?.[pixel];
            if (ranking) this.mostrarResultado(ranking); else this.mostrarResultadoBloqueado();
            if (this.painel.surpresaLiberada?.[grupoAtual()]) this.mostrarModalSurpresa(rodadaId);
            return;
        }
        const resultado = resultados?.[pixel];
        return resultado ? this.mostrarResultado(resultado) : this.mostrarResultadoBloqueado();
    }

    /** TURMA / SEU GRUPO: o botão ativo em verde cheio, o outro a 50%. */
    abas (y) {
        [['turma', 'TURMA'], ['grupo', 'SEU GRUPO']].forEach(([visao, rotulo], i) => {
            const botao = new BotaoArcade(this, [62, 642][i] + 288, y + 35, {
                rotulo, largura: 576, aoClicar: () => {
                    this.visao = visao;
                    this.renderizar();
                }
            });
            botao.setAlpha(this.visao === visao ? 1 : 0.5);
            this.guardar(botao);
        });
    }

    mostrarResultado (resultado) {
        const grupo = grupoAtual();
        const lista = this.visao === 'turma' ? resultado.turma : (resultado.grupos?.[grupo] ?? []);
        // Pixel 3 (surpresa): não é um ranking de opções, é uma linha por afirmação com os dois percentuais.
        if (this.painel.catalogo?.surpresa) return this.mostrarAfirmacoes(lista);
        const dez = lista.length > 8;
        // Pixel 3: até 14 afirmações (7 linhas), então cartões mais baixos.
        const muitas = lista.length > 10;
        const medidas = muitas ? { abas: 200, cartoes: 288, passo: 68, altura: 62 }
            : dez ? { abas: 214, cartoes: 312, passo: 82, altura: CARTAO.altura }
                : { abas: 234, cartoes: 356, passo: 88, altura: CARTAO.altura };
        this.abas(medidas.abas);
        // Ranking de cima para baixo: 1 a 5 na primeira coluna e 6 a 10 na segunda (8 itens: 1-4 e 5-8).
        const linhas = Math.ceil(lista.length / 2);
        lista.forEach((item, i) => {
            const x = COLUNAS_X[Math.floor(i / linhas)];
            const y = medidas.cartoes + (i % linhas) * medidas.passo;
            const cartao = new CartaoOpcao(this, x, y, {
                letra: String(i + 1), sufixo: '.', texto: item.rotulo, ...CARTAO, altura: medidas.altura, centrado: true, larguraTexto: 440
            }).setTocavel(false);
            const percentual = this.add.text(x + CARTAO.largura - 26, y + medidas.altura / 2, `${item.percentual}%`, ARCADE_TEXT.pontosRanking)
                .setOrigin(1, 0.5);
            this.guardar(cartao, percentual);
        });
    }

    /**
     * Resultado do Pixel 3 (Figma do jogador): cada afirmação em uma linha de texto, com dois cartões
     * embaixo, "1. Controlo" e "2. Não controlo", e o percentual de cada um à direita. São até 14
     * afirmações, então a lista rola por arrasto abaixo das abas TURMA / SEU GRUPO. A ordem é a do servidor.
     */
    mostrarAfirmacoes (lista) {
        this.abas(AFIRMACOES.abas);
        const { areaY, areaX, areaLargura, cartao, folga, entre } = AFIRMACOES;
        const area = new AreaRolavel(this, { x: areaX, y: areaY, largura: areaLargura, altura: GAME.HEIGHT - areaY, eixo: 'y' });
        this.rolagem = area;
        let y = 6;
        paraAfirmacoesDoPixel3(lista).forEach(({ afirmacao, controlo, naoControlo }) => {
            const texto = this.add.text(COLUNAS_X[0] - areaX, y, afirmacao, {
                ...ARCADE_TEXT.introducao, fontSize: '24px', wordWrap: { width: 1156 }
            }).setOrigin(0, 0);
            area.conteudo.add(texto);
            y += texto.height + folga;
            [['1', ROTULO_CONTROLO, controlo], ['2', ROTULO_NAO_CONTROLO, naoControlo]].forEach(([letra, rotulo, valor], i) => {
                const x = COLUNAS_X[i] - areaX;
                const opcao = new CartaoOpcao(this, x, y, {
                    letra, sufixo: '.', texto: rotulo, ...CARTAO, altura: cartao, centrado: true, larguraTexto: 440
                }).setTocavel(false);
                const percentual = this.add.text(x + CARTAO.largura - 26, y + cartao / 2, valor === '' ? '–' : `${valor}%`, ARCADE_TEXT.pontosRanking)
                    .setOrigin(1, 0.5);
                area.conteudo.add([opcao, percentual]);
            });
            y += cartao + entre;
        });
        area.definirTamanho(y);
    }

    mostrarResultadoBloqueado () {
        const catalogo = this.painel.catalogo;
        // Pixel 3: os cartões bloqueados são as afirmações, numeradas.
        const opcoes = catalogo?.surpresa
            ? (catalogo.afirmacoes ?? []).map((texto, i) => ({ letra: String(i + 1), sufixo: '.', texto }))
            : (catalogo?.opcoes ?? []);
        this.tituloDeSecao('RESULTADOS');
        this.guardar(this.add.text(62, 244, 'Aguarde para ver os mais escolhidos pela sua turma e seu grupo!', ARCADE_TEXT.introducao)
            .setOrigin(0, 0));
        // Y do 1º cartão e passo por modelo do Figma: 10 opções (Pixel 2), Pixel 4 e demais com 8.
        const { inicio, passo } = opcoes.length > 8 ? { inicio: 321, passo: 82 }
            : this.painel.pixel === 4 ? { inicio: 325, passo: 88 } : { inicio: 337, passo: 88 };
        opcoes.forEach((opcao, i) => {
            const x = COLUNAS_X[i % 2];
            const y = inicio + Math.floor(i / 2) * passo;
            const cartao = new CartaoOpcao(this, x, y, {
                letra: opcao.letra ?? opcao.id, ...(opcao.sufixo ? { sufixo: opcao.sufixo } : {}),
                texto: opcao.texto, ...CARTAO, centrado: true, larguraTexto: 440
            }).setEstado('bloqueada').setTocavel(false);
            // Cadeado de 28×30 em +503,6 / +18 (Figma, image 1494).
            const cadeado = this.add.image(x + 503.6, y + 18.2, TEX.LOCK_RESULTADO).setOrigin(0).setDisplaySize(28, 30)
                .setTint(0x2b2b2b);   // o SVG do projeto é claro; o do Figma (image 1494) é escuro
            this.guardar(cartao, cadeado);
        });
    }

    /* ---------- fim do módulo ---------- */

    async mostrarResolucaoCompleta (participante) {
        const senha = Symbol('resolucao');
        this.senhaTela = senha;
        this.tituloDeSecao('RESOLUÇÃO COMPLETA', 630);

        const estilo = ARCADE_TEXT.introducao;
        let y = 243;
        RESOLUCAO.forEach((paragrafo) => {
            paragrafo.forEach((linhas) => {
                const rico = criarTextoRico(this, 62, y, 633, linhas, estilo, { entrelinha: 30 });
                this.guardar(...rico.objetos);
                y += rico.altura;
            });
            y += 22;
        });

        // Pontos pelo modelo do PDF (docs/menuresolucao-integracao.md §9): Pixels 1, 2 e 4 pontuam o
        // grupo, pela participação; o Pixel 3 é a nota individual. Sem consolidação, "-" e não zero.
        const pontuacao = (await this.fonte.obterMinhaPontuacao?.({ participanteId: participante, grupoId: grupoAtual() })) ?? null;
        if (!this.ativo || this.senhaTela !== senha) return;
        const doGrupo = (pixel) => pontuacao?.grupo?.pixels?.find((x) => x.pixel === pixel)?.pontos ?? null;
        const exibir = (valor) => (valor == null ? '-' : String(valor));

        // Dois blocos no painel translúcido (Figma, frame 1410104341): em cima a pontuação do grupo
        // (Pixels 1, 2 e 4 e o total do grupo), embaixo a nota individual (Pixel 3).
        const X = 867.15, LARGURA = 338.69, PASSO = 45.15;
        const subtitulo = { ...ARCADE_TEXT.introducao, fontSize: '18px', fontStyle: '700' };
        const bloco = (topo, altura) => {
            const painel = this.add.graphics();
            painel.fillStyle(ARCADE_HEX.BRANCO, 0.5).fillRoundedRect(853, topo, 367, altura, 8);
            painel.lineStyle(1.5, ARCADE_HEX.BRANCO, 0.8).strokeRoundedRect(853, topo, 367, altura, 8);
            this.guardar(painel);
        };
        const linha = (rotulo, valor, topo) => {
            const meio = topo + 17;
            this.guardar(
                this.add.text(X, meio, rotulo, ARCADE_TEXT.placar).setOrigin(0, 0.5),
                this.add.text(X + LARGURA, meio, exibir(valor), ARCADE_TEXT.placar).setOrigin(1, 0.5)
            );
        };
        const divisoria = (y) => this.guardar(this.add.rectangle(X, y, LARGURA, 1.5, ARCADE_HEX.TINTA).setOrigin(0, 0.5));

        // Grupo: título, três Pixels, divisória e total.
        bloco(210, 252);
        this.guardar(this.add.text(X, 226, 'PONTUAÇÃO DO GRUPO', subtitulo).setOrigin(0, 0));
        [1, 2, 4].forEach((pixel, i) => linha(`PIXEL ${pixel}`, doGrupo(pixel), 258 + i * PASSO));
        divisoria(404);
        linha('TOTAL', pontuacao?.grupo?.total ?? null, 413);

        // Individual: o Pixel 3 é a nota de cada participante.
        bloco(480, 112);
        this.guardar(this.add.text(X, 496, 'SUA PONTUAÇÃO INDIVIDUAL', subtitulo).setOrigin(0, 0));
        linha('PIXEL 3', pontuacao?.pixel_3?.pontuacao ?? null, 536);

        this.guardar(this.add.text(853, 608, 'Pixels 1, 2 e 4 pontuam o grupo.\nO Pixel 3 é a sua nota individual.', {
            ...ARCADE_TEXT.introducao, fontSize: '18px', lineSpacing: 2, wordWrap: { width: 367 }
        }).setOrigin(0, 0));
    }
}
